import React, { useState, useEffect, useRef } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./ProjectSliders.css";
import axios from "axios";
import Loading from "../Loading/Loading";
import ProjectMaterialsSlider from "../ProjectMaterialsSlider/ProjectMaterialsSlider";
import ProjectListContent from "../ProjectListContent/ProjectListContent";

const CustomPrevArrow = (props) => {
  const { onClick } = props;
  return (
    <div className="custom-arrow custom-prev-arrow" onClick={onClick}>
      <img src="/images/leftarrow.svg" alt="Previous" />
    </div>
  );
};

const CustomNextArrow = (props) => {
  const { onClick } = props;
  return (
    <div className="custom-arrow custom-next-arrow" onClick={onClick}>
      <img src="/images/rightarrow.svg" alt="Next" />
    </div>
  );
};

function ProjectSliders() {
  const [projectIds, setProjectIds] = useState([]); // Store sorted project IDs
  const [projects, setProjects] = useState([]); // Store fetched projects
  const [currentIndex, setCurrentIndex] = useState(0); // Track current slide index
  const [isLoading, setIsLoading] = useState(false); // Loading state for initial fetch
  const [isSlideLoading, setIsSlideLoading] = useState(false); // Loading state for slide change
  const isFetchedProject = useRef(false); // Prevents duplicate API call
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1000);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1000);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isFetchedProject.current) {
      fetchProjectIds();
      isFetchedProject.current = true; // Ensure it runs only once
    }
  }, []);

  const fetchProjectIds = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/Projects/project-ids`
      );
      if (response.data) {
        setProjectIds(response.data);
        // Fetch the first two projects
        if (response.data.length > 0) fetchProject(response.data[0]);
        if (response.data.length > 1) fetchProject(response.data[1]);
      }
    } catch (error) {
      console.error("Error fetching project IDs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProject = async (id) => {
    setIsSlideLoading(true); // Start loading for slide change
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/Projects/${id}`
      );
      if (response.data) {
        setProjects((prevProjects) => [...prevProjects, response.data]);
      }
    } catch (error) {
      console.error("Error fetching project:", error);
    } finally {
      setIsSlideLoading(false); // Stop loading for slide change
    }
  };

  const handleSlideChange = (currentSlide) => {
    setCurrentIndex(currentSlide);

    // Get the next project ID based on the order in projectIds
    const nextProjectId = projectIds[currentSlide];
    if (
      nextProjectId &&
      !projects.some((project) => project.id === nextProjectId)
    ) {
      fetchProject(nextProjectId); // Fetch the project by ID if not already loaded
    }
  };

  const settings = {
    dots: false,
    arrows: true,
    infinite: false, // Disable infinite looping
    slidesToShow: 1,
    slidesToScroll: 1,
    draggable: isMobile,
    swipe: isMobile,
    touchMove: isMobile,
    afterChange: handleSlideChange, // Triggered when the slide changes
    prevArrow: <CustomPrevArrow />, // Custom previous arrow
    nextArrow: <CustomNextArrow />, // Custom next arrow
  };

  return (
    <div className="project-slider-container">
      {isLoading && <Loading />}{" "}
      {/* Show loading spinner during initial fetch */}
      <Slider {...settings}>
        {projectIds.map((id, index) => {
          const project = projects.find((p) => p.id === id);
          const isCurrentSlide = index === currentIndex;

          return (
            <div key={id} className="project-slide">
              {project ? (
                <>
                  {project.projectVideos &&
                    project.projectVideos.length > 0 && (
                      <ProjectMaterialsSlider
                        videos={[...project.projectVideos].sort(
                          (a, b) => a.sortOrder - b.sortOrder
                        )}
                      />
                    )}
                  <h2 className="desktop-title-project">
                    {project.name} | <span>{project.subTitle}</span>
                  </h2>
                  <div className="mobile-title-project">
                    <h2>{project.name}</h2>
                    <p>{project.subTitle}</p>
                  </div>
                  <div className="first-description">
                    <div className="client">
                      <p className="property-title">CLIENT</p>
                      <p className="property-value">{project.client}</p>
                    </div>
                    <div className="market">
                      <p className="property-title">MARKET</p>
                      <p className="property-value">{project.market}</p>
                    </div>
                    <div className="focus">
                      <p className="property-title">FOCUS</p>
                      <p className="property-value">{project.focus}</p>
                    </div>
                  </div>
                  <ProjectListContent content={project.projectsListContent} />
                </>
              ) : (
                // Show loading spinner only for the current slide being fetched
                isCurrentSlide &&
                isSlideLoading && (
                  <div className="loading-slide">
                    <Loading />
                  </div>
                )
              )}
            </div>
          );
        })}
      </Slider>
    </div>
  );
}

export default ProjectSliders;
